import { getScript } from '../../utils/getScript';

declare const facebookConnectPlugin: PhonegapFacebookPlugin.FacebookConnectPluginStatic;

export interface FBLoginResponse{
  authResponse?: {
    accessToken: string;
    expiresIn: string;
    secret: string;
    session_key: boolean;
    sig: string;
    userID: string;
  };
  status: string;
}

interface FacebookLogin {
  getLoginStatus: () => Promise<FBLoginResponse>;
  login: (retry?: boolean) => Promise<PhonegapFacebookPlugin.LoginResult>;
  getEmail: () => Promise<string>;
}

const PERMISSIONS_APP = ['email'];
const PERMISSIONS = 'email,public_profile';

export class CordovaFbLogin implements FacebookLogin {
  getLoginStatus = () => {
    return new Promise(facebookConnectPlugin.getLoginStatus);
  };
  login = (retry = false): Promise<PhonegapFacebookPlugin.LoginResult> => {
    return new Promise((resolve, reject) => {
      facebookConnectPlugin.login(PERMISSIONS_APP, resolve, reject);
    });
  };
  getEmail = () => {
    const graphQuery = '/me?fields=email';
    return new Promise<string>((resolve, reject) => {
      facebookConnectPlugin.api(graphQuery, PERMISSIONS_APP, (emailResponse) => {
        resolve(emailResponse.email);
      }, reject);
    });
  };
}

interface MyWindow extends Window {
  FB?: any;
}

declare const window: MyWindow;

export class WebFbLogin implements FacebookLogin {
  get fb() {
    if (window.FB) {
      return Promise.resolve(window.FB);
    }
    return getScript('https://connect.facebook.net/en_US/sdk.js')
      .then(() => window.FB as Facebook)
      .then((FB) => {
        window.FB.init({
          appId: FB_APP_ID,
          xfbml: false,
          status: true,
          cookie: true,
          version: 'v2.12',
        });
        return FB;
      });
  }

  getLoginStatus = () => {
    const doGetLogin = () => new Promise((resolve, reject) => {
      if (!window.FB.getAuthResponse || !window.FB.getAuthResponse()) {
        return resolve({ status: 'not connected' });
      }
      return resolve(window.FB.getAuthResponse());
    }) as Promise<FBLoginResponse>;

    if (window.FB) {
      return doGetLogin();
    }
    return this.fb.then(FB => doGetLogin());
  };
  login = (retry = false) => {
    const doLogin = () => {
      return new Promise((resolve, reject) => window.FB.login((response) => {
        const errorStatus = ['not_authorized', 'no_permission'];
        if (!response.status || errorStatus.includes(response.status)) {
          return reject(response);
        }
        return resolve(response);
      }, {
        scope: PERMISSIONS,
        return_scopes: true,
        auth_type: retry ? 'rerequest' : null,
      })) as Promise<PhonegapFacebookPlugin.LoginResult>;
    };

    if (window.FB) {
      return doLogin();
    }
    return this.fb.then(doLogin);
  };
  getEmail = () => {
    const graphQuery = '/me?fields=email';
    return this.fb.then(FB =>
      new Promise(resolve => window.FB.api(graphQuery, (emailResponse) => {
        resolve(emailResponse.email);
      }))) as Promise<string>;
  };
}
